import { useState } from "react"
import PropTypes from "prop-types"
import { Button, Flex, Modal } from "antd"
import { CloseOutlined } from "@ant-design/icons"
import { isFeatureEnabled } from "config/features"
import { PAYMENT_OPTIONS, WAIVER_OPTIONS, CHECKIN_OPTIONS } from "./constants"
import styles from "./ReservationFilterModal.module.css"

// Check-in/out is implemented but hidden behind the CHECK_IN_OUT flag until the client signs off.
const isCheckInEnabled = isFeatureEnabled("CHECK_IN_OUT")

const FilterToggleGroup = ({ options, value, onChange }) => (
  <Flex gap={8} wrap="wrap">
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        className={`${styles.toggleBtn} ${value === option.value ? styles.toggleBtnActive : ""}`}
        onClick={() => onChange(value === option.value ? null : option.value)}
      >
        {option.label}
      </button>
    ))}
  </Flex>
)

FilterToggleGroup.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired
}

const ReservationFilterModal = ({ open, filters, onApply, onClear, onClose }) => {
  const [localFilters, setLocalFilters] = useState(filters)

  const handleOpen = () => setLocalFilters(filters)

  const setFilter = (key, value) => setLocalFilters((prev) => ({ ...prev, [key]: value }))

  const activeCount = [localFilters.payment, localFilters.waivers, isCheckInEnabled && localFilters.checkIn]
    .filter(Boolean).length

  return (
    <Modal
      open={open}
      onCancel={onClose}
      afterOpenChange={(isOpen) => isOpen && handleOpen()}
      footer={null}
      closable={false}
      width={420}
      styles={{ body: { padding: 0 } }}
    >
      <Flex vertical className={styles.modal}>
        <Flex align="center" justify="space-between" className={styles.modalHeader}>
          <span className={styles.modalTitle}>Filters</span>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close filters">
            <CloseOutlined />
          </button>
        </Flex>

        <Flex vertical gap={0} className={styles.modalBody}>
          <Flex vertical gap={10} className="mt-s">
            <span className={styles.sectionLabel}>Payment</span>
            <FilterToggleGroup options={PAYMENT_OPTIONS} value={localFilters.payment} onChange={(value) => setFilter("payment", value)} />
          </Flex>

          <Flex vertical gap={10} className="mt-s">
            <span className={styles.sectionLabel}>Waivers/Documents</span>
            <FilterToggleGroup options={WAIVER_OPTIONS} value={localFilters.waivers} onChange={(value) => setFilter("waivers", value)} />
          </Flex>

          {isCheckInEnabled && (
            <Flex vertical gap={10} className="mt-s">
              <span className={styles.sectionLabel}>Check-in</span>
              <FilterToggleGroup options={CHECKIN_OPTIONS} value={localFilters.checkIn} onChange={(value) => setFilter("checkIn", value)} />
            </Flex>
          )}
        </Flex>

        <Flex gap={8} align="center" className="pt-xl">
          <Button
            color="primary"
            variant="outlined"
            onClick={() => {
              onClear()
              setLocalFilters((prev) => ({ ...prev, payment: null, waivers: null, checkIn: null }))
            }}
          >
            Clear all
          </Button>
          <Button type="primary" onClick={() => onApply(localFilters)}>
            {activeCount > 0 ? `Show results (${activeCount})` : "Show results"}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  )
}

ReservationFilterModal.propTypes = {
  open: PropTypes.bool.isRequired,
  filters: PropTypes.shape({
    payment: PropTypes.string,
    waivers: PropTypes.string,
    checkIn: PropTypes.string
  }).isRequired,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

export default ReservationFilterModal
